// Slack API wrapper using authenticated axios instance
import api from '../services/api';

/**
 * Helper function to make API calls using the authenticated axios instance
 * This ensures all Slack API calls include Stytch authentication headers
 */
async function apiCall(endpoint: string, options: { method?: string; body?: any } = {}) {
  console.log(`📞 Slack API Call: ${options.method || 'GET'} ${endpoint}`, options.body ? `with body` : '');

  try {
    let response;
    const method = (options.method || 'GET').toUpperCase();
    
    switch (method) {
      case 'POST':
        response = await api.post(endpoint, options.body);
        break;
      case 'PUT':
        response = await api.put(endpoint, options.body);
        break;
      case 'DELETE':
        response = await api.delete(endpoint);
        break;
      case 'GET':
      default:
        response = await api.get(endpoint);
        break;
    }
    
    return response.data;
  } catch (error: any) {
    console.error(`Slack API Error: ${error.message}`, error.response?.data);
    throw error;
  }
}

/**
 * Slack API interface for frontend components
 */
export const slackApi = {
  /**
   * Check backend health
   */
  async health() {
    try {
      return await apiCall('/slack/health');
    } catch (error) {
      console.error('Backend health check failed:', error);
      throw error;
    }
  },

  /**
   * Get OAuth authorization URL for Slack workspace connection
   */
  async getAuthUrl(organizationId: string) {
    try {
      const data = await apiCall(`/slack/auth-url?organizationId=${encodeURIComponent(organizationId)}`);
      return data; // Should return { url: string }
    } catch (error) {
      console.error('Failed to get Slack auth URL:', error);
      throw error;
    }
  },

  /**
   * Get connection status for organization's Slack workspaces
   */
  async getConnectionStatus(organizationId?: string) {
    try {
      const queryParam = organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : '';
      const data = await apiCall(`/slack/connection-status${queryParam}`);
      return data; // Should return { connected: boolean, workspaces: any[], count: number }
    } catch (error) {
      console.error('Failed to get Slack connection status:', error);
      throw error;
    }
  },

  /**
   * Get users from a Slack workspace
   */
  async getWorkspaceUsers(teamId: string) {
    try {
      const data = await apiCall(`/slack/workspace/users?teamId=${encodeURIComponent(teamId)}`);
      return data; // Should return { users: any[] }
    } catch (error) {
      console.error('Failed to get Slack workspace users:', error);
      throw error;
    }
  },

  /**
   * Get channels from a Slack workspace
   */
  async getWorkspaceChannels(teamId: string) {
    try {
      const data = await apiCall(`/slack/workspace/channels?teamId=${encodeURIComponent(teamId)}`);
      return data; // Should return { channels: any[] }
    } catch (error) {
      console.error('Failed to get Slack workspace channels:', error);
      throw error;
    }
  },

  /**
   * Test connection to a Slack workspace
   */
  async testConnection(teamId: string) {
    try {
      const data = await apiCall(`/slack/workspace/test?teamId=${encodeURIComponent(teamId)}`);
      return data; // Should return { success: boolean, botInfo?: any, error?: string }
    } catch (error) {
      console.error('Failed to test Slack connection:', error);
      throw error;
    }
  },

  /**
   * Send a direct message (for testing)
   */
  async sendDirectMessage(teamId: string, slackUserId: string, message: string) {
    try {
      const data = await apiCall('/slack/send-dm', {
        method: 'POST',
        body: JSON.stringify({ teamId, slackUserId, message })
      });
      return data; // Should return { success: boolean, messageTs?: string, error?: string }
    } catch (error) {
      console.error('Failed to send Slack DM:', error);
      throw error;
    }
  },

  /**
   * Disconnect a Slack workspace
   */
  async disconnectWorkspace(teamId: string) {
    try {
      const data = await apiCall('/slack/disconnect', {
        method: 'POST',
        body: JSON.stringify({ teamId })
      });
      return data; // Should return { success: boolean, message: string }
    } catch (error) {
      console.error('Failed to disconnect Slack workspace:', error);
      throw error;
    }
  },

  /**
   * Get all connected workspaces (admin)
   */
  async getAllWorkspaces() {
    try {
      const data = await apiCall('/slack/workspaces');
      return data; // Should return { workspaces: any[] }
    } catch (error) {
      console.error('Failed to get all Slack workspaces:', error);
      throw error;
    }
  }
}; 